import { ContentWriteGreySvg } from '@/assets/image/free-to-pay/content-write-grey-svg';
import { TrialRetouchMagicWand } from '@/assets/image/free-to-pay/trial-retouch-magic-wand-svg';
import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import { IS_PYNE_AVATAR } from '@/features/flags/legacy-flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { panelOpenAtom } from '@/hooks/useTutorial';
import { trackPyneOnboardingRendered } from '@/lib/analytics/scribe/pyne';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import { DialogFooter, DialogHeader } from '@heidi/design-system/dialog';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  TypographyCaption,
  TypographyH5,
  TypographyP,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { ModalId, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

export function FreeTrialModal() {
  const setTutorialPanelOpen = useSetAtom(panelOpenAtom);

  const { isOpen: isQueueOpen, handleClose: handleQueueClose } = useQueuedModal(
    ModalId.FREE_TRIAL
  );
  const appState = useAtomValue(appStateForModalsAtom);

  const { t } = useTranslations();
  const region = IS_PYNE_AVATAR();

  useEffect(() => {
    const shouldClose =
      appState.userTier === SubscriptionTier.ENTERPRISE ||
      appState.userTier === SubscriptionTier.PRO_PLUS;

    if (shouldClose && isQueueOpen) {
      handleQueueClose();
    }
  }, [appState.userTier, isQueueOpen, handleQueueClose]);

  // Apple-compliant builds never show billing UI; drain the queue so it doesn't stall.
  useEffect(() => {
    if (isAppleCompliantBuild() && isQueueOpen) {
      handleQueueClose();
    }
  }, [isQueueOpen, handleQueueClose]);

  // Users with affiliate codes get 30 days, others get 14 days
  const { data: user } = useQueryUser((data) => ({
    affiliate_code: data?.affiliate_signup_code,
  }));
  const trialDaysLeft = user?.affiliate_code ? 30 : 14;

  const handleClose = () => {
    handleQueueClose();

    if (!region) {
      setTutorialPanelOpen(true);
      return;
    }

    try {
      if (typeof window !== 'undefined' && typeof window.pyne === 'function') {
        const tourId =
          region === 'germany'
            ? '51a31f21-c32c-4efd-83cc-5e2cf6385ea9'
            : '4f5c0b0d-e100-4b6b-9981-52700dfa9ce9';

        window.pyne('startTour', tourId);
        trackPyneOnboardingRendered({ region });
        logger.info(`Pyne Tour Started for ${region}`);
      } else {
        logger.info('Pyne is not available on window yet.');
      }
    } catch (error) {
      logger.error('Error starting Pyne tour:', error);
    }
  };

  if (isAppleCompliantBuild()) return null;

  if (
    appState.userTier === SubscriptionTier.ENTERPRISE ||
    appState.userTier === SubscriptionTier.PRO_PLUS
  ) {
    return null;
  }

  return (
    <DialogDrawer open={isQueueOpen} onOpenChange={handleClose}>
      <DialogDrawerContent
        className="mb-auto flex h-fit flex-col items-center gap-6 border-0 p-4 sm:w-[500px] sm:p-8"
        overlayClassName="backdrop-blur-[3px]"
      >
        <DialogHeader className="flex flex-col items-center justify-center">
          <FramedIcon state="pro" />
          <DialogTitle className="text-center text-[28px] sm:text-3xl">
            {t('reverseTrial.title', { days: trialDaysLeft })}
          </DialogTitle>
          <TypographyPBold className="mb-1 text-center text-text-secondary">
            {t('reverseTrial.subtitle')}
          </TypographyPBold>
        </DialogHeader>
        <article className="flex w-full flex-wrap items-stretch justify-between gap-3">
          <section className="flex flex-1 flex-col gap-2 rounded-lg border p-4 sm:p-6">
            <ContentWriteGreySvg alt="content-write" width={40} height={40} />
            <div className="flex flex-col gap-1">
              <TypographyH5>{t('reverseTrial.evenBetterNotes')}</TypographyH5>
              <TypographyP>{t('reverseTrial.evenBetterNotesDescription')}</TypographyP>
            </div>
          </section>
          <section className="flex flex-1 flex-col gap-2 rounded-lg border p-4 sm:p-6">
            <TrialRetouchMagicWand alt="retouch-magic-wand" width={40} height={40} />
            <div className="flex flex-col gap-1">
              <TypographyH5>{t('reverseTrial.tailoredToYou')}</TypographyH5>
              <TypographyP>{t('reverseTrial.tailoredToYouDescription')}</TypographyP>
            </div>
          </section>
        </article>
        <DialogFooter className="w-full">
          <Button
            size="lg"
            className="w-full"
            variant="default"
            onClick={() => {
              handleClose();
            }}
          >
            {t('reverseTrial.getStarted')}
          </Button>
        </DialogFooter>
        <DialogFooter className="w-full px-4 text-center sm:justify-center sm:px-14">
          <TypographyCaption className="text-text-secondary">
            {t('reverseTrial.footerText')}
          </TypographyCaption>
        </DialogFooter>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
